import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  createUploadJobIdempotencyKey,
  getUploadJobEvents,
  getUploadJobSnapshot,
  listUploadJobs as listRemoteUploadJobs,
  sendUploadJobCommand,
  uploadJobsToProcessingItems,
} from "./uploadJobActivity";
import {
  applyUploadJobEvent,
  createUploadJobStore,
  getUploadJob,
  listUploadJobs,
  reconcileUploadJobSnapshot,
  type UploadJobStore,
} from "./uploadJobStore";
import type { UploadJobCommand } from "./uploadJobContract";
import type { DocumentProcessingItem } from "./documentProcessingActivity";

const DEFAULT_POLL_INTERVAL_MS = 5_000;

type UseUploadJobActivityOptions = {
  enabled?: boolean;
  pollIntervalMs?: number;
};

type UploadJobCommandInput =
  | { type: "cancel_file"; fileItemId: string }
  | { type: "retry_file"; fileItemId: string }
  | { type: "acknowledge_job" };

async function replayJobEvents(
  client: ReturnType<typeof useApiClient>["client"],
  patientId: string,
  store: UploadJobStore,
  jobId: string,
): Promise<UploadJobStore> {
  let nextStore = store;
  let current = getUploadJob(nextStore, jobId);
  if (!current) return nextStore;

  let afterSequence = current.lastEventSequence;
  let hasMore = true;
  while (hasMore) {
    const response = await getUploadJobEvents(
      client,
      patientId,
      jobId,
      afterSequence,
    );
    for (const rawEvent of response.events) {
      nextStore = applyUploadJobEvent(nextStore, rawEvent);
    }
    current = getUploadJob(nextStore, jobId);
    afterSequence = response.nextSequence;
    hasMore = response.hasMore;
    if (!response.events.length) break;
  }
  return nextStore;
}

function findJobForFileItem(store: UploadJobStore, fileItemId: string) {
  return listUploadJobs(store).find((job) =>
    job.items.some((item) => item.id === fileItemId),
  );
}

export function useUploadJobActivity({
  enabled = true,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
}: UseUploadJobActivityOptions = {}) {
  const { client, patientId } = useApiClient();
  const [store, setStore] = useState<UploadJobStore>(() =>
    createUploadJobStore(),
  );
  const [syncError, setSyncError] = useState<Error | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const storeRef = useRef(store);
  const refreshInFlightRef = useRef(false);

  const replaceStore = useCallback((nextStore: UploadJobStore) => {
    storeRef.current = nextStore;
    setStore(nextStore);
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled || !patientId || refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    setIsSyncing(true);
    try {
      const response = await listRemoteUploadJobs(client, patientId);
      let nextStore = storeRef.current;

      // The list discovers new jobs. Existing jobs are advanced through their
      // ordered event stream so a refresh cannot silently skip transitions.
      for (const snapshot of response.jobs) {
        const current = getUploadJob(nextStore, snapshot.id);
        if (!current) {
          nextStore = reconcileUploadJobSnapshot(nextStore, snapshot);
          continue;
        }

        if (snapshot.lastEventSequence > current.lastEventSequence) {
          try {
            nextStore = await replayJobEvents(
              client,
              patientId,
              nextStore,
              snapshot.id,
            );
          } catch {
            // A gap, deleted event window, or malformed event is recovered by
            // the authoritative snapshot before the next polling cycle.
            const latest = await getUploadJobSnapshot(
              client,
              patientId,
              snapshot.id,
            );
            nextStore = reconcileUploadJobSnapshot(nextStore, latest);
          }
        }

        nextStore = reconcileUploadJobSnapshot(nextStore, snapshot);
      }

      // A job can disappear from includeCompleted=false between list calls as
      // it completes. Replay its remaining events once so the UI can render
      // the terminal state before naturally hiding it.
      for (const job of listUploadJobs(nextStore)) {
        if (job.acknowledgedAt) continue;
        try {
          nextStore = await replayJobEvents(
            client,
            patientId,
            nextStore,
            job.id,
          );
        } catch {
          // The next list response remains the recovery source if polling is
          // interrupted; keep the last known state visible meanwhile.
        }
      }

      replaceStore(nextStore);
      setSyncError(null);
    } catch (error) {
      setSyncError(
        error instanceof Error
          ? error
          : new Error("Could not refresh document activity"),
      );
    } finally {
      refreshInFlightRef.current = false;
      setIsSyncing(false);
    }
  }, [client, enabled, patientId, replaceStore]);

  useEffect(() => {
    if (!enabled || !patientId) {
      replaceStore(createUploadJobStore());
      return;
    }

    void refresh();
    const interval = setInterval(() => {
      void refresh();
    }, pollIntervalMs);
    return () => clearInterval(interval);
  }, [enabled, patientId, pollIntervalMs, refresh, replaceStore]);

  const runCommand = useCallback(
    async (jobId: string, command: UploadJobCommandInput) => {
      if (!patientId) return null;
      const response = await sendUploadJobCommand(client, patientId, jobId, {
        ...command,
        idempotencyKey: createUploadJobIdempotencyKey(),
      } as UploadJobCommand);
      replaceStore(reconcileUploadJobSnapshot(storeRef.current, response.job));
      setSyncError(null);
      return response;
    },
    [client, patientId, replaceStore],
  );

  const cancelFile = useCallback(
    (fileItemId: string) => {
      const job = findJobForFileItem(storeRef.current, fileItemId);
      if (!job) return Promise.resolve(null);
      return runCommand(job.id, { type: "cancel_file", fileItemId });
    },
    [runCommand],
  );

  const retryFile = useCallback(
    (fileItemId: string) => {
      const job = findJobForFileItem(storeRef.current, fileItemId);
      if (!job) return Promise.resolve(null);
      return runCommand(job.id, { type: "retry_file", fileItemId });
    },
    [runCommand],
  );

  const acknowledgeJob = useCallback(
    (jobId: string) => runCommand(jobId, { type: "acknowledge_job" }),
    [runCommand],
  );

  const jobs = useMemo(() => listUploadJobs(store), [store]);
  const processingItems = useMemo(
    () => uploadJobsToProcessingItems(jobs),
    [jobs],
  );

  return {
    jobs,
    processingItems: processingItems as DocumentProcessingItem[],
    refresh,
    cancelFile,
    retryFile,
    acknowledgeJob,
    isSyncing,
    syncError,
  };
}
